import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { moduleNotFound, waitingForProcessing } from '../WaitProcess';
import { DORPGS_LIST_TEXT, PAGE_COMMON_TEXT } from '../lang';
import { useGetDsrcAssessmentID } from '../../graphql';
import { getApplication } from '../../../Application/graphql';
import { PAGE_TEXT } from '../../../Assessment/lang';
import {
  AcademicAssessments,
  PublicationAssessments,
  MeritAssessments,
  ScoresOnApplicant,
  DeclarationAssessments,
  PanelInterview,
  AcademicRefereeConfirmation,
  RefereeTickMode,
} from '../../../Assessment/DRCAssessment';
import {
  Loading,
  Alert,
  type ListSections,
  SideList,
  Accordion,
} from '../../../../components';
import { Admission } from '../../../Assessment/DRCAssessment/components/Admission';

export const DSRCAssessmentForm: React.FunctionComponent = () => {
  const TITLE = PAGE_COMMON_TEXT.SIDEBAR_TEXT.DORPGS_ASSESSMENT;
  const { applicationId = '' } = useParams();

  const [clickedForm, setClickedForm] = useState<string>(DORPGS_LIST_TEXT.GSB1);
  const handleViewForm = (item: string): void => {
    setClickedForm(item);
  };
  // check whether the application has related assessment
  const {
    dsrcAssessmentId,
    isUPAF,
    recommendForHKPFS,
    loading: dsrcAssessmentIDLoading,
    error: dsrcAssessmentIDError,
  } = useGetDsrcAssessmentID(applicationId, {
    application: { _id: { eq: applicationId } },
  });

  const hasDsrcAssessment = Boolean(dsrcAssessmentId.dsrcAssessmentId);
  const hasDsrcAdmission = Boolean(dsrcAssessmentId.dsrcAdmissionId);
  const dsrcAssessed = hasDsrcAssessment || hasDsrcAdmission;

  const {
    applicationData,
    remarks: returnedRemarks,
    loading: applicationLoading,
    error: applicationError,
  } = getApplication(applicationId);

  const { academicQualifications, programmeChoice, publications, merits } =
    applicationData;

  const { entryYear } = programmeChoice; // used for getting registration deadline

  if (dsrcAssessmentIDError ?? applicationError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` +
            dsrcAssessmentIDError?.message +
            applicationError?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const loading = dsrcAssessmentIDLoading || applicationLoading;
  const {
    GSB1,
    ACADEMIC_QUALIFICATION,
    PUBLICATION,
    MERIT,
    SCORES_ON_APPLICANT,
    DECLARATION,
    REFEREE_REPORT,
    INTERVIEW_PANEL,
  } = DORPGS_LIST_TEXT;

  const componentContainerMap: Record<string, React.ReactNode> = {
    [GSB1]: hasDsrcAdmission ? (
      <Admission isViewMode={true} entryYear={entryYear} />
    ) : (
      moduleNotFound(TITLE + ' - ' + GSB1)
    ),

    [REFEREE_REPORT]: (
      <AcademicRefereeConfirmation
        refereeTickMode={RefereeTickMode.ViewMode}
        refetchAssessment={() => {}}
      />
    ),
    [INTERVIEW_PANEL]: (
      <Accordion
        className="w-full !mt-4"
        disableExpand
        defaultExpanded
        title={PAGE_TEXT.DRC_ASSESSMENT_DETAILS.PANEL_INFORMATION.TITLE}
        details={<PanelInterview refetchAssessment={() => {}} isViewMode />}
      />
    ),

    [ACADEMIC_QUALIFICATION]: hasDsrcAssessment ? (
      <AcademicAssessments
        isViewMode={true}
        academicQualificationIds={
          academicQualifications?.map(
            item => item?.academicQualificationId as string,
          ) ?? []
        }
      />
    ) : (
      moduleNotFound(TITLE + ' - ' + ACADEMIC_QUALIFICATION)
    ),

    [PUBLICATION]: (
      <Accordion
        className="w-full pt-4"
        defaultExpanded
        title={PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_B.TITLE}
        details={
          hasDsrcAssessment ? (
            <PublicationAssessments
              isViewMode={true}
              publicationIds={
                publications?.map(item => item?.publicationId as string) ?? []
              }
            />
          ) : (
            moduleNotFound(TITLE + ' - ' + PUBLICATION)
          )
        }
      />
    ),
    [MERIT]: (
      <Accordion
        className="w-full pt-4"
        defaultExpanded
        title={PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_C.TITLE}
        details={
          hasDsrcAssessment ? (
            <MeritAssessments isViewMode={true} meritIds={merits} />
          ) : (
            moduleNotFound(TITLE + ' - ' + MERIT)
          )
        }
      />
    ),

    [SCORES_ON_APPLICANT]: hasDsrcAssessment ? (
      <ScoresOnApplicant isViewMode={true} />
    ) : (
      moduleNotFound(TITLE + ' - ' + SCORES_ON_APPLICANT)
    ),
    [DECLARATION]: hasDsrcAssessment ? (
      <DeclarationAssessments
        isViewMode
        returnedRemarks={returnedRemarks}
        recommendForHKPFS={recommendForHKPFS}
      />
    ) : (
      moduleNotFound(TITLE + ' - ' + DECLARATION)
    ),
  };

  const listSections: ListSections[] = [
    {
      items: [
        GSB1,
        REFEREE_REPORT,
        ...(isUPAF ? [INTERVIEW_PANEL] : []),
        DECLARATION,
      ],
    },
  ];

  return loading ? (
    <Loading />
  ) : dsrcAssessed ? (
    <>
      <div className="grid grid-cols-[4fr,1fr]" style={{ marginTop: -12 }}>
        <div className="mt-2">{componentContainerMap[clickedForm]}</div>
        <div className="mt-6">
          <SideList
            listSections={listSections}
            activatedForm={clickedForm}
            onChangeActivated={handleViewForm}
          />
        </div>
      </div>
    </>
  ) : (
    waitingForProcessing(TITLE)
  );
};
